import { defineStore } from 'pinia';
import { isEmpty, isEqual } from 'lodash-es';
import {
  complianceReport,
  sendActionableInsightsShareLinks,
  complianceReportDownload,
  employerBrandReportDownload,
  jobPerformanceReportDownload,
  employerBrandReport,
  jobPerformanceReport,
} from '@/api/insights';
import { removeEmptyParams } from '@/stores/filters/helpers';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppPagination } from '@/stores/app/pagination';
import { useAppRoute } from '@/stores/app/route';
import { useAppLoading } from '@/stores/app/loading';
import { ref } from 'vue';
import { mergeFilters } from '@/stores/insights/helpers';
import { useInsightsProject } from '@/stores/insights/insightsProject';
import { useDatasetCounts } from '@/stores/insights/datasetCounts';
import { useFilters } from '../filters/filters';

export const useActionableInsights = defineStore('actionableInsights', () => {
  // Other stores
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();
  const { updatePagination } = useAppPagination();
  const routeStore = useAppRoute();
  const { setLoading } = useAppLoading();
  const { setDatasetCounts } = useDatasetCounts();

  // State
  const selectedReportJobs = ref([]);
  const documents = ref([]);
  const totals = ref({});
  const exclusions = ref({});
  const documentType = ref('job');

  // Actions
  const setSelectedReportJobs = (jobs = []) => {
    selectedReportJobs.value = jobs;
  };

  const setActionableInsightsData = (actionableInsight) => {
    documents.value = actionableInsight?.documents || [];
    totals.value = actionableInsight?.totals || {};
    exclusions.value = actionableInsight?.exclusions || {};
  };

  const toggleDocumentType = (optionalValue) => {
    if (optionalValue) documentType.value = optionalValue;
    else {
      documentType.value = documentType.value === 'job' ? 'template' : 'job';
    }
  };

  const getComplianceReport = async (
    {
      routeQuery,
      selectedFilters,
      page,
      sort,
      router,
      selectedMyJobs,
      resetData,
    },
  ) => {
    if (resetData) setSelectedReportJobs([]);

    const params = routeQuery || {};

    params.page = page || undefined;

    const {
      filters,
      pagination,
      owned,
      totals: respTotals,
      documents: respDocuments,
      sorting,
      exclusions: respExclusions,
      projectImageUrl,
      projectName,
    } = await complianceReport({
      params,
      selectedMyJobs,
      selectedFilters,
      sort,
    });

    const subMenuOptions = sorting;

    if (!isEmpty(sorting) && !respDocuments?.length) {
      subMenuOptions.disabled = true;
    }

    if (respTotals?.totalDocsAffected) {
      setDatasetCounts(respTotals.totalDocsAffected);
    }

    /* Send relevant updates to filters */
    useFilters().updateFilters({
      filters,
      myJobsEnabled: owned,
      subMenuOptions,
    });

    setActionableInsightsData({
      documents: respDocuments,
      totals: respTotals,
      exclusions: respExclusions,
    });

    updatePagination(pagination);

    useInsightsProject().setSharedProjectInfo({
      newProjectImageUrl: projectImageUrl,
      newProjectName: projectName,
    });

    const prunedRootStateQuery = removeEmptyParams(routeStore.route?.query);
    const prunedRouteQuery = removeEmptyParams(routeQuery);

    /* update URL with shareable filter link using router.replace */
    if (
      router
      && (!isEqual(prunedRootStateQuery, prunedRouteQuery))
    ) {
      const queryFilters = mergeFilters(filters);

      const newQuery = {
        ...params,
        ...queryFilters,
        from: undefined,
        to: undefined,
        legacy: undefined,
        sort: sorting.value,
      };

      if (selectedMyJobs) {
        newQuery.owned = selectedMyJobs;
      }

      router.push({
        query: newQuery,
      }).catch(() => true); // ignore duplicate navigation error
    }
  };

  const getEmployerBrandReport = async (
    {
      routeQuery,
      selectedFilters,
      page,
      sort,
      router,
      selectedMyJobs,
      resetData,
    },
  ) => {
    if (resetData) setSelectedReportJobs([]);

    const params = routeQuery || {};
    const isTemplate = documentType.value === 'template';

    params.page = page || undefined;

    const {
      filters,
      pagination,
      owned,
      totals: respTotals,
      documents: respDocuments,
      sorting,
      exclusions: respExclusions,
      projectImageUrl,
      projectName,
    } = await employerBrandReport({
      params,
      selectedMyJobs,
      selectedFilters,
      sort,
      isTemplate,
    });

    const subMenuOptions = sorting;

    if (!isEmpty(sorting) && !respDocuments?.length) {
      subMenuOptions.disabled = true;
    }

    if (respTotals?.totalDocsAffected) {
      setDatasetCounts(respTotals.totalDocsAffected);
    }

    /* Send relevant updates to filters */
    useFilters().updateFilters({
      filters,
      myJobsEnabled: owned,
      subMenuOptions,
    });

    setActionableInsightsData({
      documents: respDocuments,
      totals: respTotals,
      exclusions: respExclusions,
    });

    updatePagination(pagination);

    useInsightsProject().setSharedProjectInfo({
      newProjectImageUrl: projectImageUrl,
      newProjectName: projectName,
    });

    const prunedRootStateQuery = removeEmptyParams(routeStore.route?.query);
    const prunedRouteQuery = removeEmptyParams(routeQuery);

    /* update URL with shareable filter link using router.replace */
    if (
      (router
      && (!isEqual(prunedRootStateQuery, prunedRouteQuery)))
      || (!routeQuery.templates && isTemplate)
      || (routeQuery.templates && !isTemplate)
    ) {
      const queryFilters = mergeFilters(filters);

      const newQuery = {
        ...params,
        ...queryFilters,
        from: undefined,
        to: undefined,
        sort: sorting.value,
        legacy: undefined,
      };

      if (selectedMyJobs) {
        newQuery.owned = selectedMyJobs;
      }

      if (isTemplate) {
        newQuery.templates = true;
      } else if (newQuery.templates) {
        delete newQuery.templates;
      }

      router.push({
        query: newQuery,
      }).catch(() => true); // ignore duplicate navigation error
    }
  };

  const getJobPerformanceReport = async (
    {
      routeQuery,
      selectedFilters,
      page,
      sort,
      router,
      selectedMyJobs,
      resetData,
    },
  ) => {
    if (resetData) setSelectedReportJobs([]);

    const params = routeQuery || {};

    params.page = page || undefined;

    const {
      filters,
      pagination,
      owned,
      totals: respTotals,
      documents: respDocuments,
      sorting,
      projectImageUrl,
      projectName,
    } = await jobPerformanceReport({
      params,
      selectedMyJobs,
      selectedFilters,
      sort,
    });

    const subMenuOptions = sorting;

    if (!isEmpty(sorting) && !respDocuments?.length) {
      subMenuOptions.disabled = true;
    }

    if (respTotals?.totalDocsAffected) {
      setDatasetCounts(respTotals.totalDocsAffected);
    }

    /* Send relevant updates to filters */
    useFilters().updateFilters({
      filters,
      myJobsEnabled: owned,
      subMenuOptions,
    });

    setActionableInsightsData({ documents: respDocuments, totals: respTotals });
    updatePagination(pagination);
    useInsightsProject().setSharedProjectInfo({
      newProjectImageUrl: projectImageUrl,
      newProjectName: projectName,
    });

    const prunedRootStateQuery = removeEmptyParams(routeStore.route?.query);
    const prunedRouteQuery = removeEmptyParams(routeQuery);

    /* update URL with shareable filter link using router.replace */
    if (
      router
      && (!isEqual(prunedRootStateQuery, prunedRouteQuery))
    ) {
      const queryFilters = mergeFilters(filters);

      const newQuery = {
        ...params,
        ...queryFilters,
        from: undefined,
        to: undefined,
        sort: sorting.value,
        legacy: undefined,
      };

      if (selectedMyJobs) {
        newQuery.owned = selectedMyJobs;
      }

      router.push({
        query: newQuery,
      }).catch(() => true); // ignore duplicate navigation error
    }
  };

  const downloadComplianceReport = async ({
    filters, dateRange, myJobsEnabled, document, sort,
  }) => {
    try {
      complianceReportDownload({
        document, filters, dateRange, myJobsEnabled, sort,
      });
    } catch (error) {
      addErrorSnackbar({ text: `Compliance Report Download Error: ${error.message}` });
      throw error;
    }
  };

  const downloadEmployerBrandReport = async ({
    filters, dateRange, myJobsEnabled, document, sort,
  }) => {
    try {
      employerBrandReportDownload({
        document,
        filters,
        dateRange,
        myJobsEnabled,
        sort,
        isTemplate: documentType.value === 'template',
      });
    } catch (error) {
      addErrorSnackbar({ text: `Employer Brand Report Download Error: ${error.message}` });
      throw error;
    }
  };

  const downloadJobPerformanceReport = async ({
    filters, dateRange, myJobsEnabled, document, sort,
  }) => {
    try {
      jobPerformanceReportDownload({
        document, filters, dateRange, myJobsEnabled, sort,
      });
    } catch (error) {
      addErrorSnackbar({ text: `Job Performance Report Download Error: ${error.message}` });
      throw error;
    }
  };

  const shareActionableReportViaEmail = async ({
    userId, note, selectedJobs, reportUrl, scheduledAt, name, routeName,
  }) => {
    const REPORT_IDS = {
      'compliance-report': 'complianceReportNew',
      'employer-brand-report': 'employerBrandReport',
      'job-performance-report': 'jobPerformanceReport',
    };

    try {
      setLoading({ modalLoading: true });

      const finalSelectedJobs = selectedJobs.map((job) => ({
        id: job.id,
        title: job.title,
        url: `${window.location.origin}/app/view/${job.id}`,
        issues: job.issues,
      }));

      const { success, error, details } = await sendActionableInsightsShareLinks({
        userId,
        note,
        selectedJobs:
        finalSelectedJobs,
        reportUrl,
        scheduledAt,
        reportId: REPORT_IDS[routeName],
      });

      if (success && !scheduledAt) {
        addSuccessSnackbar({
          text: `The report was shared with ${name}.`,
        });
      }

      if (!success && error && details) {
        addErrorSnackbar({
          text: `Failed to send email - ${error}`,
        });
      }
    } catch (error) {
      addErrorSnackbar({
        text: `Share failed - ${error.message}`,
      });
      throw error;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  return {
    selectedReportJobs,
    documents,
    totals,
    exclusions,
    documentType,
    setSelectedReportJobs,
    setActionableInsightsData,
    toggleDocumentType,
    getComplianceReport,
    getEmployerBrandReport,
    getJobPerformanceReport,
    downloadComplianceReport,
    downloadEmployerBrandReport,
    downloadJobPerformanceReport,
    shareActionableReportViaEmail,
  };
});
